/**
 * GlobalBackendUnavailableModal - Context-driven global modal
 * 
 * Automatically appears when BackendStatusContext detects 
 * multiple 5xx errors. Works across all pages.
 */
import React, { useEffect } from 'react';
import { useBackendStatus, setGlobalBackendStatusReporter } from '../contexts/BackendStatusContext';
import { AlertTriangle, RefreshCw, Home, Server, Wifi, Clock, X } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

const GlobalBackendUnavailableModal = () => {
  const { 
    isBackendUnavailable, 
    isRecovering, 
    lastError,
    errorCount,
    dismissModal, 
    retryAndDismiss,
    reportBackendError,
    reportBackendSuccess
  } = useBackendStatus();
  
  const navigate = useNavigate();
  
  // Register global reporters so API layer can report errors
  useEffect(() => {
    setGlobalBackendStatusReporter(reportBackendError, reportBackendSuccess);
    return () => setGlobalBackendStatusReporter(null, null);
  }, [reportBackendError, reportBackendSuccess]);
  
  if (!isBackendUnavailable) {
    return null;
  }
  
  const handleBackToDashboard = () => {
    dismissModal();
    navigate('/home');
  };
  
  // If recovering, show a brief success state
  if (isRecovering) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <div className="bg-slate-800 border border-emerald-500/50 rounded-2xl p-8 max-w-md mx-4 shadow-2xl">
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mb-4">
              <RefreshCw className="w-8 h-8 text-emerald-400 animate-spin" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">
              Connection Restored
            </h2>
            <p className="text-slate-400">
              Reconnecting to server...
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  const status = lastError?.statusCode || 0;
  const classification = status === 520 ? 'GATEWAY_520' : 
                         status === 0 ? 'NETWORK_FAILURE' : 
                         `SERVER_${status}`;
  
  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      data-testid="backend-unavailable-modal"
    >
      <div className="relative bg-slate-800 border border-slate-600 rounded-2xl p-8 max-w-md mx-4 shadow-2xl">
        {/* Close button */}
        <button
          onClick={dismissModal}
          className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"
          aria-label="Close"
          data-testid="backend-modal-close-btn"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="flex flex-col items-center text-center">
          {/* Icon */}
          <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mb-4">
            <Server className="w-8 h-8 text-amber-400" />
          </div>
          
          {/* Title */}
          <h2 className="text-xl font-semibold text-white mb-2">
            Server Temporarily Unavailable
          </h2>
          
          {/* Status indicator */}
          <div className="w-full bg-orange-950/30 border border-orange-600/30 rounded-lg p-4 mb-4">
            <p className="text-sm text-orange-200 font-medium">
              {status === 520 ? 'Gateway Error (520)' : 
               status === 502 ? 'Bad Gateway (502)' :
               status === 0 ? 'Network Connection Lost' :
               `Server Error (${status})`}
            </p>
            <p className="text-xs text-orange-300/70 mt-2">
              {classification === 'GATEWAY_520' 
                ? 'The request did not reach the server. This is typically a proxy or edge issue.'
                : classification === 'NETWORK_FAILURE'
                ? 'Unable to connect to the server. Check your internet connection.'
                : 'The server encountered an error processing your request.'}
            </p>
          </div>
          
          {/* Reassurance */}
          <div className="w-full text-left text-sm text-slate-300 mb-4">
            <p className="font-medium text-green-400">✓ Your session is preserved</p>
            <p className="text-xs text-slate-400 mt-1">
              You are still logged in. No data has been lost.
            </p>
          </div>
          
          {/* Tips */}
          <div className="w-full space-y-2 mb-6 text-sm text-slate-400">
            <div className="flex items-center gap-2">
              <Wifi className="w-4 h-4 text-slate-500" />
              <span>Check your internet connection</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-slate-500" />
              <span>Wait a moment and try again</span>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 w-full">
            <Button
              onClick={retryAndDismiss}
              className="flex-1 gap-2 bg-orange-600 hover:bg-orange-500"
              data-testid="backend-unavailable-retry-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Reload Page
            </Button>
            <Button
              onClick={handleBackToDashboard}
              variant="outline"
              className="flex-1 gap-2 border-slate-600 text-slate-300 hover:bg-slate-800"
              data-testid="backend-unavailable-dashboard-btn"
            >
              <Home className="w-4 h-4" />
              Back to Home
            </Button>
          </div>
          
          {/* Error count indicator */}
          {errorCount > 0 && (
            <p className="mt-4 text-xs text-slate-500">
              {errorCount} connection error{errorCount > 1 ? 's' : ''} detected
            </p>
          )}
          
          {/* Technical details */}
          {lastError && (
            <details className="w-full mt-4 text-xs text-slate-500 text-left">
              <summary className="cursor-pointer hover:text-slate-400">Technical Details</summary>
              <pre className="mt-2 p-2 bg-slate-950 rounded text-xs overflow-auto max-h-24 font-mono">
                {JSON.stringify({
                  status: lastError.statusCode,
                  url: lastError.url,
                  timestamp: new Date(lastError.timestamp).toISOString(),
                }, null, 2)}
              </pre>
            </details>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalBackendUnavailableModal;
